<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <BackButton label="Zurück zur Übersicht" :margin-bottom="0" @click="emit('back')"/>

      <div v-if="contractDocument" class="d-flex align-items-center">
        <span class="badge me-2" :class="contractDocument.signedByUser ? 'text-bg-success' : 'text-bg-warning'">
          {{ isBsgSide ? 'Mitarbeiter' : 'Ihre Unterschriften' }}:
          {{ contractDocument.signaturesByUser }} / {{ contractDocument.requiredSignaturesByUser }}
        </span>
        <span class="badge" :class="contractDocument.signedByBsg ? 'text-bg-success' : 'text-bg-warning'">
          BSG: {{ contractDocument.signaturesByBsg }} / {{ contractDocument.requiredSignaturesByBsg }}
        </span>
      </div>
    </div>

    <Loader v-if="isLoading"/>

    <div v-if="contractDocument && !isSignedBySide" class="alert alert-info">
      Bitte lesen Sie das Dokument und unterschreiben Sie an allen mit „Unterschreiben“ markierten Stellen{{
        isBsgSide ? ' für die BSG' : ''
      }}.
    </div>

    <!-- HTML is rendered by the backend (Twig, values escaped there) -->
    <div v-if="contractDocument"
         class="contract-document"
         v-html="contractDocument.html"
         @click="handleDocumentClick">
    </div>

    <Modal
        v-model="showSignatureModal"
        title="Digitale Unterschrift"
        sizeModal="modal-md"
        :hide-footer="true">
      <ContractSignatureForm :is-saving="isSaving" @sign="signDocument"/>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import {computed, defineEmits, defineProps, onMounted, ref} from 'vue';
import BackButton from '@/components/button/BackButton.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import userContractService from '@/api/UserContract';
import ContractSignatureForm from '@/views/App/MyContracts/ContractSignatureForm.vue';

// isBsgSide: HR signs the BSG places, otherwise the external user signs his places (both draw the signature)
const props = defineProps<{ documentId: number, isBsgSide: boolean }>();
const emit = defineEmits<{ (e: 'back'): void, (e: 'signed', document: any): void }>();

const contractDocument = ref<any>(null);
const showSignatureModal = ref(false);
const signPosition = ref<number | null>(null);
const isSaving = ref(false);
const isLoading = ref(true);

const isSignedBySide = computed(() =>
    props.isBsgSide ? contractDocument.value?.signedByBsg : contractDocument.value?.signedByUser
);

async function loadDocument() {
  try {
    const {data} = await userContractService.getDocument(props.documentId);
    contractDocument.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isLoading.value = false;
  }
}

// Sign buttons are part of the backend HTML, so clicks are caught here (event delegation)
function handleDocumentClick(event: MouseEvent) {
  const button = (event.target as HTMLElement).closest<HTMLElement>('.contract-sign-button');
  if (!button || button.dataset.side !== (props.isBsgSide ? 'bsg' : 'user')) {
    return;
  }

  signPosition.value = Number(button.dataset.position);
  showSignatureModal.value = true;
}

async function signDocument(digitalSignature: string) {
  if (signPosition.value === null || isSaving.value) {
    return;
  }

  const signData = {position: signPosition.value, digitalSignature};

  isSaving.value = true;
  try {
    const {data} = props.isBsgSide
        ? await userContractService.signDocumentBsg(props.documentId, signData)
        : await userContractService.signDocument(props.documentId, signData);
    contractDocument.value = data.data;
    showSignatureModal.value = false;

    SuccessToast(isSignedBySide.value ? 'Dokument vollständig unterschrieben.' : 'Unterschrift gespeichert.');
    emit('signed', contractDocument.value);
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSaving.value = false;
  }
}

onMounted(loadDocument);
</script>

<style scoped>
.contract-document {
  background-color: #fff;
  border: 1px solid #dee2e6;
  padding: 40px;
}

.contract-document :deep(.contract-sign-button) {
  margin-top: 5px;
}

@media (max-width: 768px) {
  .contract-document {
    padding: 15px;
  }
}
</style>
